import { Flexbox } from '@/ui/flexbox/flexbox';
import { OutsideClickHandler } from '@/ui/outside-click-handler/outside-click-handler';
import { DetailBlock } from './detail-block';
import { ProjectHeaderFirstRow } from './project-header-first-row';
import { ProjectHeaderSkeleton } from './project-header-skeleton';
import {
  isProjectHeaderHovered,
  isProjectHeaderEditMode,
} from './project-header.state';
import type { ProjectHeaderProps } from './project-header';
import { formatDate, parseDateRange } from '@/helpers/format-date';
import { useMemo } from 'react';
import { Inline } from '@/ui/line/line';
import { styled } from 'goober';
import { useFormContext } from 'react-hook-form';
import type { ProjectHeaderForm } from './edit/use-project-header-form';
import { useProject } from '../use-project.query';
import { CreatedForSelect } from './edit/created-for-select';
import { TimeFramePicker } from './edit/timeframe-picker';
import { OwnerSelect } from './edit/owner-select';
import { ProjectHeaderSecondRow } from './project-header-second-row';
import { useSetAtom, useAtom, useAtomValue } from 'jotai';
import { projectIdState } from '../../project.state';

type ProjectHeaderLeftBlockProps = ProjectHeaderProps;

export const ProjectHeaderLeftBlock = ({
  isLoading,
  isCreateNewMode = false,
  onEditSubmit,
}: ProjectHeaderLeftBlockProps) => {
  const projectId = useAtomValue(projectIdState);
  const { data: project } = useProject(projectId);
  const {
    handleSubmit,
    formState: { isDirty, isValid },
  } = useFormContext<ProjectHeaderForm>();
  const setIsHovered = useSetAtom(isProjectHeaderHovered);
  const [isEditMode, setIsEditMode] = useAtom(isProjectHeaderEditMode);

  const detailsContent = useMemo(() => {
    const { team, owner } = project ?? {};

    return [
      {
        title: 'Created for',
        value: team?.name ?? '--',
        isEditable: true,
        editModeComponent: <CreatedForSelect />,
      },
      {
        title: 'Stage',
        value: project?.mostAdvancedListingStatus ?? '--',
      },
      {
        title: 'Owner',
        value: owner ? `${owner.firstName} ${owner.lastName}` : '--',
        isEditable: true,
        editModeComponent: <OwnerSelect />,
      },
      {
        title: 'Creation date',
        value: project?.createdDate
          ? formatDate(project.createdDate, { format: 'MM yyyy' })
          : '--',
      },
      {
        title: 'Timeframe',
        value:
          project?.timeframeStart && project?.timeframeEnd
            ? parseDateRange(project.timeframeStart, project.timeframeEnd)
            : '--',
        isEditable: true,
        editModeComponent: <TimeFramePicker />,
      },
    ];
  }, [project]);

  const handleOutsideClick = async () => {
    if (isCreateNewMode) {
      return await handleSubmit(onEditSubmit)();
    }

    if (isDirty && isValid) {
      await handleSubmit(onEditSubmit)();
    }

    setIsEditMode(false);
  };

  return (
    <OutsideClickHandler onOutsideClick={handleOutsideClick}>
      <Flexbox
        direction="column"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {isLoading ? (
          <ProjectHeaderSkeleton />
        ) : (
          <>
            <ProjectHeaderFirstRow isCreateNewMode={isCreateNewMode} />
            <ProjectHeaderSecondRow
              projectStatus={project?.status}
              projectType={project?.type}
              isCreateNewMode={isCreateNewMode}
            />

            <ContentBox
              justifyContent="flex-start"
              alignItems={isEditMode ? 'self-end' : 'flex-start'}
            >
              {detailsContent?.map(contentBlock => (
                <DetailBlock key={contentBlock.title} {...contentBlock} />
              ))}
            </ContentBox>
          </>
        )}
      </Flexbox>
    </OutsideClickHandler>
  );
};

const ContentBox = styled(Inline)`
  margin-top: 24px;
  gap: 20px;

  @media (min-width: 1440px) {
    gap: 50px;
  }
`;
